import {useFx, useSize, loading, color} from '../fx.js';
import * as THREE from 'three'
import {Background} from '../bg.js';
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import RefractionMaterial from '../refraction-material'
import lerp from '@14islands/lerp'

const { WebGLRenderTarget, Object3D, Vector2, Vector3 } = THREE
const cameraFov = 50
const cameraZ = 30
const bgZ = -cameraZ*2
const widthFactor = 0.3
const heightFactor = .7
function Diamonds({readyFun, layers = 1}) {
	const fx = useFx();
	const size = useSize()
	const {camera, frameRun, gl, scene} = fx
	const [model, setModel] = useState(false)
	const [geometry, setGeometry] = useState(false)
	const [backfaceFbo, setBackfaceFbo] = useState(false)
	const [backfaceMaterial, setBackfaceMaterial] = useState(false)
	const [envFbo, setEnvFbo] = useState(false)
	const [refractionMaterial, setRefractionMaterial] = useState(false)
	const isNoSuspend = fx.bgAnimation === 'zoomInfinity'
	const rendered = useRef(false);
	const lastTime = useRef(0);
	const calcTime = useRef(0);
	const dummy = useMemo(() => new Object3D(), [])
	const {width, height} = useMemo(() => {
		const h = 2 * Math.abs(cameraZ - bgZ) * Math.tan(Math.PI/180*cameraFov/2);
		const w = h * size.width / size.height
		return {width: w * widthFactor, height: h * heightFactor}
	},[size.width, size.height])
	const diamonds = useMemo(() => {
		return new Array(30).fill().map((_, i) => {
			const position = [
				i < 5 ? 0 : width / 2 - Math.random() * width,
				Math.random() * 40,
				i < 5 ? 35 : Math.random() * 35
			];
			const dhFactor = 2 - position[2] / 35
			position[2] -= 20;
			return {
				position,
				dhFactor,
				factor: 0.1 + Math.random(),
				direction: Math.random() < 0.5 ? -1 : 1,
				rotation: [
					Math.sin(Math.random()) * Math.PI,
					Math.sin(Math.random()) * Math.PI,
					Math.cos(Math.random()) * Math.PI,
				],
			}
		})

	}, [width])
	useEffect(() => {
		if(fx.diamondsGeometry === 'brilliant'){
			return loading({
				loader: new GLTFLoader(),
				url: fx.script.baseUri + '/assets/3d/diamond-brilliant.glb',
				fun: (gltf)=>{
					if(gltf){
						const geom = gltf.scene.children[0].geometry
						const sc = 1.5
						geom.scale(sc, sc, sc)
						setGeometry(geom)
					}
					readyFun()
				}
			})
		}else if(fx.diamondsGeometry === 'emerald'){
			return loading({
				loader: new OBJLoader(),
				url: fx.script.baseUri + '/assets/3d/emerald.obj',
				fun: (object)=>{
					if(object){
						object.traverse( function ( child ) {
							if ( child instanceof THREE.Mesh ) {
								const sc = 0.12
								child.geometry.scale(sc, sc, sc)
								setGeometry(child.geometry)
							}
						})
					}
					readyFun()
				}
			})
		}else if(fx.diamondsGeometry === 'cube' || !fx.diamondsGeometry){
			const sz = 1.5
			const geom = new THREE.BoxGeometry( sz, sz, sz )
			setGeometry(geom)
			readyFun()
			return () => {
				geom.dispose()
			}
		}
	},[fx.script.baseUri, fx.diamondsGeometry, readyFun])
	useEffect(() => {
		const defines = {}
		if(fx.reflectionColor){
			defines.USE_FRESNEL = ''
		}
		const refractionMaterial = new RefractionMaterial({
			defines,
			uniforms: {
				envMap: { value: null },
				backfaceMap: { value: null },
				resolution: { value: new Vector2(1, 1) },
				reflectionColor: { value: new Vector3(0,0,0) },
				fresnelRange: { value: 0.85 }
			},
		})
		setRefractionMaterial(refractionMaterial)
		return () => {
			refractionMaterial.dispose()
		}
	}, [fx.reflectionColor]);
	useEffect(() => {
		const backfaceMaterial = new RefractionMaterial({
			defines: {
				IS_BACKFACE: ''
			},
			side: THREE.BackSide
		})
		setBackfaceMaterial(backfaceMaterial)
		return () => {
			backfaceMaterial.dispose()
		}
	}, []);
	useEffect(() => {
		if(geometry && refractionMaterial){
			const model = new THREE.InstancedMesh(geometry, refractionMaterial, diamonds.length)
			model.layers.set(layers)
			scene.add(model)
			setModel(model)
			return () => {
				scene.remove(model)
			}
		}
	}, [geometry, diamonds.length, layers, scene, refractionMaterial]);
	useEffect(() => {
		const w = size.width;
		const h = size.height;
		const envFbo = new WebGLRenderTarget(w, h)
		const backfaceFbo = new WebGLRenderTarget(w, h)
		rendered.current = false;
		setEnvFbo(envFbo)
		setBackfaceFbo(backfaceFbo)
		return () => {
			envFbo.dispose()
			backfaceFbo.dispose()
		}
	}, [size.width, size.height]);
	useEffect(() => {
		if(backfaceFbo && refractionMaterial){
			refractionMaterial.uniforms.backfaceMap.value = backfaceFbo.texture;
		}
	}, [refractionMaterial, backfaceFbo]);
	useEffect(() => {
		if(refractionMaterial && envFbo){
			refractionMaterial.uniforms.envMap.value = envFbo.texture
		}
	}, [refractionMaterial, envFbo]);
	useEffect(() => {
		if(refractionMaterial && fx.reflectionColor){
			refractionMaterial.uniforms.reflectionColor.value = color(fx.reflectionColor)
		}
	}, [fx.reflectionColor, refractionMaterial]);
	useEffect(() => {
		const w = size.width;
		const h = size.height;
		if(refractionMaterial){
			refractionMaterial.uniforms.resolution.value.x = w;
			refractionMaterial.uniforms.resolution.value.y = h;
		}
		rendered.current = false;
	}, [size.width, size.height, refractionMaterial])
	useEffect(()=>{
		if(model && envFbo && backfaceFbo && refractionMaterial && diamonds){
			const dw = width / 2
			const dhBase = height / 2
			return frameRun((t)=>{
				if(
					(!isNoSuspend) && rendered.current && fx.isScrollOut()
				){
					lastTime.current = t;
					return;
				}
				rendered.current = true;
				let dt = t - lastTime.current;
				if(dt < 0){
					dt = 0
				}
				lastTime.current = t;
				calcTime.current += dt;
				diamonds.forEach((data, i) => {
					const dh = dhBase * data.dhFactor;
					data.position[1] -= 10 * data.factor * dt * data.direction
					if (data.direction === 1 ? data.position[1] < -dh : data.position[1] > dh)
						data.position = [
							i < 5 ? 0 : dw - Math.random() * width,
							dh * data.direction,
							data.position[2],
						]
					const { position, rotation, factor } = data
					dummy.position.set(position[0], position[1], position[2])
					dummy.rotation.set(rotation[0] + calcTime.current * factor, rotation[1] + calcTime.current * factor, rotation[2] + calcTime.current * factor)
					dummy.scale.set(1 + factor, 1 + factor, 1 + factor)
					dummy.updateMatrix()
					model.rotation.x = lerp(model.rotation.x, (0.5 - fx.mouse.y), 0.002, dt)
					model.rotation.y = lerp(model.rotation.y, (fx.mouse.x - 0.5), 0.002, dt)
					model.setMatrixAt(i, dummy.matrix)
				})
				model.instanceMatrix.needsUpdate = true

				camera.layers.set(0)
				gl.setRenderTarget(envFbo)
				gl.render(scene, camera)
				camera.layers.set(layers)
				model.material = backfaceMaterial
				gl.setRenderTarget(backfaceFbo)
				gl.render(scene, camera)

				camera.layers.enable(0)
				gl.setRenderTarget(null)
				model.material = refractionMaterial
			})
		}
	}, [width, height, frameRun, model, envFbo, backfaceFbo, backfaceMaterial, refractionMaterial, fx, diamonds, camera, dummy, gl, layers, scene, isNoSuspend])

	return null
}
const bgPosition = new Vector3(0,0,bgZ)
function App(){
	const fx = useFx()
	const [isBgReady, setIsBgReady] = useState(false)
	const [isDiamondsReady, setIsDiamondsReady] = useState(false)
	const bgReady = useCallback(()=>{
		setIsBgReady(true)
	},[])
	const diamondsReady = useCallback(()=>{
		setIsDiamondsReady(true)
	},[])
	useEffect(() => {
		if(isBgReady && isDiamondsReady){
			fx.ready()
		}
	},[isBgReady, isDiamondsReady, fx])
	return(
		<>
			<Background position={bgPosition} readyFun={bgReady} />
			<Diamonds layers={1} readyFun={diamondsReady} />
		</>
	)
}
const EffectDiamonds = {
	App,
	config: {
		createCamera: ()=>{
			const cam = new THREE.PerspectiveCamera(cameraFov, 1, 0.1, 1000)
			cam.position.z = cameraZ
			cam.lookAt(0, 0, 0)
			return cam
		}
	}
}
export {EffectDiamonds, Diamonds};
